import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TopAlgorithmsChart from '../Chart/TopAlgorithmsChart/TopAlgorithmsChart';
import { Doughnut } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { FaDollarSign, FaFireAlt, FaUsers, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import './AlgorithmList.css';

const AlgorithmList = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [doughnutData, setDoughnutData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [expandedAlgo, setExpandedAlgo] = useState(null);
  const [coins, setCoins] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/miners-by-algo');
        const allData = response.data.data;

        const sortedData = allData.sort((a, b) => b.total_miners - a.total_miners);
        setData(sortedData);

        const top10Data = sortedData.slice(0, 10);
        const chartData = {
          labels: top10Data.map((item) => item.algo),
          datasets: [
            {
              data: top10Data.map((item) => parseInt(item.total_miners, 10)),
              backgroundColor: generateColors(top10Data.length),
              hoverBackgroundColor: generateColors(top10Data.length, true),
            },
          ],
        };
        setDoughnutData(chartData);
      } catch (err) {
        console.error('Error fetching data', err);
      }
    };
    fetchData();
  }, []);

  const generateColors = (count, hover = false) => {
    const baseColors = [
      '#FF5733',
      '#33FF57',
      '#337BFF',
      '#FFC300',
      '#C70039',
      '#900C3F',
      '#DAF7A6',
      '#581845',
      '#1ABC9C',
      '#F39C12',
    ];
    return baseColors.slice(0, count).map((color) => (hover ? `${color}CC` : color));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedData = [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setData(sortedData);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const filteredData = data.filter((item) =>
    item.algo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const top10ByBTC = [...data]
    .sort((a, b) => b.avg_24h_btc - a.avg_24h_btc)
    .slice(0, 10)
    .map((item) => item.algo);

  const top10ByMiners = [...data]
    .sort((a, b) => b.total_miners - a.total_miners)
    .slice(0, 10)
    .map((item) => item.algo);

  const toggleCoinsTable = async (algo) => {
    if (expandedAlgo === algo) {
      setExpandedAlgo(null);
    } else {
      setExpandedAlgo(algo);
      try {
        const response = await axios.get(`/api/currencies/algorithm?algo=${algo}`);
        const uniqueCoins = response.data.filter(
          (coin, index, self) => index === self.findIndex((c) => c.symbol === coin.symbol)
        );
        setCoins(uniqueCoins);
      } catch (err) {
        console.error('Error fetching coins data:', err);
      }
    }
  };

  return (
<div className="AlgorithmList-container">
<h2>Algorithm Miner Statistics</h2>
<p className="description">
  Mining algorithms are at the core of cryptocurrency mining. They determine how miners solve complex problems to validate transactions and secure the blockchain. Below is a detailed overview of the most popular and profitable mining algorithms.
  Icons indicate specific features:
</p>

<div className="icon-legend">
  <p>
    <FaFireAlt className="icon-fire" /> <strong>Hot Algorithm:</strong> Represents highly active algorithms with significant miner activity.
  </p>
  <p>
    <FaDollarSign className="icon-dollar" /> <strong>Profitable Algorithm:</strong> Indicates algorithms with higher average 24-hour BTC rewards.
  </p>
  <p>
    <FaUsers className="icon-users" /> <strong>Popular Algorithm:</strong> Highlights algorithms with a large number of miners.
  </p>
</div>

  {doughnutData && (
    <div className="card">
      <div className="card-header">
        <h3>Top 10 Miner Distribution by Algorithm</h3>
      </div>
      <div className="card-body">
        <div className="chart-wrapper">
          <Doughnut data={doughnutData} />
        </div>
      </div>
    </div>
  )}

  <div className="card table-card">
    <div className="card-header">
      <h3>All Algorithms and Miners</h3>
      <input
        type="text"
        placeholder="Search by Algorithm..."
        className="search-input"
        value={searchTerm}
        onChange={handleSearch}
      />
    </div>
    <div className="card-body">
      <table className="AlgorithmList-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('algo')}>
              Algorithm {getSortIcon('algo')}
            </th>
            <th onClick={() => handleSort('total_miners')}>
              Miners {getSortIcon('total_miners')}
            </th>
            <th onClick={() => handleSort('unique_coins')}>
              Coins {getSortIcon('unique_coins')}
            </th>
            <th onClick={() => handleSort('total_pools')}>
              Pools {getSortIcon('total_pools')}
            </th>
            <th onClick={() => handleSort('avg_24h_btc')}>
              Avg 24h BTC {getSortIcon('avg_24h_btc')}
            </th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((item, index) => (
            <React.Fragment key={index}>
              <tr className={index < 3 && currentPage === 1 ? 'hot-algo' : ''}>
                <td data-label="Algorithm">
                  <Link to={`/algorithmhardware/${item.algo}`} className="algorithm-link">
                    {item.algo}{' '}
                    {top10ByBTC.includes(item.algo) && <FaDollarSign className="icon-dollar" />}
                    {top10ByMiners.includes(item.algo) && <FaFireAlt className="icon-fire" />}
                    {top10ByMiners.includes(item.algo) && <FaUsers className="icon-users" />}
                  </Link>
                </td>
                <td data-label="Miners">{item.total_miners}</td>
                <td
                  data-label="Coins"
                  onClick={() => toggleCoinsTable(item.algo)}
                  className="unique-coins"
                >
                  {item.unique_coins}
                </td>
                <td data-label="Pools">{item.total_pools}</td>
                <td data-label="Avg 24h BTC">{parseFloat(item.avg_24h_btc).toFixed(8)}</td>
              </tr>
              {expandedAlgo === item.algo && (
                <tr>
                  <td colSpan="5">
                    <table className="coins-table">
                      <thead>
                        <tr>
                          <th>Symbol</th>
                          <th>Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coins.map((coin, coinIndex) => (
                          <tr key={coinIndex}>
                            <td>
                              <Link to={`/coin/${coin.symbol}`}>{coin.symbol}</Link>
                            </td>
                            <td>{coin.name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>

    <div className="card-footer pagination">
      <button
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-btn"
      >
        &laquo;
      </button>
      {Array.from({ length: Math.ceil(filteredData.length / itemsPerPage) }).map((_, i) => (
        <button
          key={i}
          onClick={() => paginate(i + 1)}
          className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
        >
          {i + 1}
        </button>
      ))}
      <button
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
        className="pagination-btn"
      >
        &raquo;
      </button>
    </div>
      <div className="AlgorithmList-hart-section">
        <h3>Algorithm Miner Trends (Last 30 Days)</h3>
        <p className="AlgorithmList-chart-description">
          This chart illustrates the trends in miner activity for the top algorithms over the past 30 days. It highlights how the number of miners has fluctuated, offering insights into which algorithms are gaining or losing popularity among miners. Use this data to analyze mining behavior and strategize your investments or operations accordingly.
        </p>
        <TopAlgorithmsChart />
    </div>
  </div>
</div>

  );
};

export default AlgorithmList;
